import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { authClient, NeonAuthUIProvider } from './lib/auth';

import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ThemeProvider } from './contexts/ThemeContext';
import TenantLayout from './layouts/TenantLayout';
import RequireAuth from './components/auth/RequireAuth';
import AuthGuard from './components/auth/AuthGuard';
import AuthPageWrapper from './components/auth/AuthPageWrapper';
import { GTMPageTracker } from './components/GTMPageTracker';

const AdminImages = lazy(() =>
  import('./pages/admin/AdminImages').then((module) => ({ default: module.AdminImages }))
);
const AdminPortfolio = lazy(() =>
  import('./pages/admin/AdminPortfolio').then((module) => ({ default: module.AdminPortfolio }))
);
const AdminOrders = lazy(() =>
  import('./pages/admin/AdminOrders').then((module) => ({ default: module.AdminOrders }))
);
const AdminProducts = lazy(() =>
  import('./pages/admin/AdminProducts').then((module) => ({ default: module.AdminProducts }))
);
const AdminCustomers = lazy(() =>
  import('./pages/admin/AdminCustomers').then((module) => ({ default: module.AdminCustomers }))
);
const AdminAnalytics = lazy(() =>
  import('./pages/admin/AdminAnalytics').then((module) => ({ default: module.AdminAnalytics }))
);
const AdminFeeds = lazy(() =>
  import('./pages/admin/AdminFeeds').then((module) => ({ default: module.AdminFeeds }))
);
const AdminGiftCards = lazy(() =>
  import('./pages/admin/AdminGiftCards').then((module) => ({ default: module.AdminGiftCards }))
);
const AdminUsers = lazy(() =>
  import('./pages/admin/AdminUsers').then((module) => ({ default: module.AdminUsers }))
);
const AdminCategories = lazy(() =>
  import('./pages/admin/AdminCategories').then((module) => ({ default: module.AdminCategories }))
);
const AdminPromotions = lazy(() =>
  import('./pages/admin/AdminPromotions').then((module) => ({ default: module.AdminPromotions }))
);
const AdminEvents = lazy(() =>
  import('./pages/admin/AdminEvents').then((module) => ({ default: module.AdminEvents }))
);
const EventModeFallback = lazy(() =>
  import('./pages/admin/EventModeFallback').then((module) => ({ default: module.EventModeFallback }))
);
const AdminSettings = lazy(() =>
  import('./pages/admin/AdminSettings').then((module) => ({ default: module.AdminSettings }))
);
const WebsiteStorefront = lazy(() =>
  import('./pages/admin/WebsiteStorefront').then((module) => ({ default: module.WebsiteStorefront }))
);
const PaymentSettings = lazy(() =>
  import('./pages/admin/PaymentSettings').then((module) => ({ default: module.PaymentSettings }))
);
const BillingSettings = lazy(() =>
  import('./pages/admin/BillingSettings').then((module) => ({ default: module.BillingSettings }))
);
const InventoryPage = lazy(() =>
  import('./pages/admin/Inventory').then((module) => ({ default: module.InventoryPage }))
);
const SupplyShopPage = lazy(() =>
  import('./pages/admin/SupplyShop').then((module) => ({ default: module.SupplyShopPage }))
);
const StullerCatalogPage = lazy(() =>
  import('./pages/admin/StullerCatalog').then((module) => ({ default: module.StullerCatalogPage }))
);
const AdminPOS = lazy(() =>
  import('./pages/admin/AdminPOS').then((module) => ({ default: module.AdminPOS }))
);
const AdminTransactions = lazy(() =>
  import('./pages/admin/AdminTransactions').then((module) => ({ default: module.AdminTransactions }))
);
const StorefrontSettings = lazy(() =>
  import('./pages/admin/settings/StorefrontSettings').then((module) => ({ default: module.StorefrontSettings }))
);
const WaiversSettings = lazy(() =>
  import('./pages/admin/settings/WaiversSettings').then((module) => ({ default: module.WaiversSettings }))
);
const ServicesSettings = lazy(() =>
  import('./pages/admin/settings/ServicesSettings').then((module) => ({ default: module.ServicesSettings }))
);
const AvailabilitySettings = lazy(() =>
  import('./pages/admin/settings/AvailabilitySettings').then((module) => ({ default: module.AvailabilitySettings }))
);
const CalendarSyncSettings = lazy(() =>
  import('./pages/admin/settings/CalendarSyncSettings').then((module) => ({ default: module.CalendarSyncSettings }))
);
const AddonCatalogSettings = lazy(() =>
  import('./pages/admin/settings/AddonCatalogSettings').then((module) => ({ default: module.AddonCatalogSettings }))
);
const NotificationsSettings = lazy(() =>
  import('./pages/admin/settings/NotificationsSettings').then((module) => ({ default: module.NotificationsSettings }))
);
const BackupSettings = lazy(() =>
  import('./pages/admin/settings/BackupSettings').then((module) => ({ default: module.BackupSettings }))
);
const AppearanceSettings = lazy(() =>
  import('./pages/admin/settings/AppearanceSettings').then((module) => ({ default: module.AppearanceSettings }))
);
const AboutSettings = lazy(() =>
  import('./pages/admin/settings/AboutSettings').then((module) => ({ default: module.AboutSettings }))
);
const SecuritySettings = lazy(() =>
  import('./pages/admin/settings/SecuritySettings').then((module) => ({ default: module.SecuritySettings }))
);
const WorkspacesSettings = lazy(() =>
  import('./pages/admin/settings/WorkspacesSettings').then((module) => ({ default: module.WorkspacesSettings }))
);
const AdminTestimonials = lazy(() =>
  import('./pages/admin/AdminTestimonials').then((module) => ({ default: module.AdminTestimonials }))
);
const AdminReviews = lazy(() =>
  import('./pages/admin/AdminReviews').then((module) => ({ default: module.AdminReviews }))
);
const AdminReports = lazy(() =>
  import('./pages/admin/AdminReports').then((module) => ({ default: module.AdminReports }))
);
const StudioModePage = lazy(() =>
  import('./pages/admin/StudioMode').then((module) => ({ default: module.StudioMode }))
);
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const DebugAuth = lazy(() => import('./pages/DebugAuth'));
const RootRedirect = lazy(() => import('./components/auth/RootRedirect'));
const OnboardingGuard = lazy(() => import('./components/auth/OnboardingGuard'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const TenantDashboardEntry = lazy(() => import('./pages/TenantDashboardEntry'));
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const EmailVerified = lazy(() => import('./pages/auth/EmailVerified'));
const AcceptInvite = lazy(() =>
  import('./pages/auth/AcceptInvite').then((module) => ({ default: module.AcceptInvite }))
);
const StorefrontPage = lazy(() =>
  import('./pages/storefront/StorefrontPage').then((module) => ({ default: module.StorefrontPage }))
);
const StorefrontProducts = lazy(() =>
  import('./pages/storefront/StorefrontProducts').then((module) => ({ default: module.StorefrontProducts }))
);
const StorefrontCMSPage = lazy(() =>
  import('./pages/storefront/StorefrontCMSPage').then((module) => ({ default: module.StorefrontCMSPage }))
);
const StorefrontProductDetail = lazy(() =>
  import('./pages/storefront/StorefrontProductDetail').then((module) => ({ default: module.StorefrontProductDetail }))
);
const StorefrontCart = lazy(() =>
  import('./pages/storefront/StorefrontCart').then((module) => ({ default: module.StorefrontCart }))
);
const StorefrontCheckout = lazy(() =>
  import('./pages/storefront/StorefrontCheckout').then((module) => ({ default: module.StorefrontCheckout }))
);
const StorefrontOrderConfirmation = lazy(() =>
  import('./pages/storefront/StorefrontOrderConfirmation').then((module) => ({ default: module.StorefrontOrderConfirmation }))
);
const StorefrontBookingPage = lazy(() =>
  import('./pages/storefront/StorefrontBookingPage').then((module) => ({ default: module.StorefrontBookingPage }))
);
const StorefrontWaiverPage = lazy(() =>
  import('./pages/storefront/StorefrontWaiverPage').then((module) => ({ default: module.StorefrontWaiverPage }))
);

function CustomLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
      Loading...
    </div>
  );
}

function RouteView({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function AppContent() {
  const navigate = useNavigate();

  return (
    <NeonAuthUIProvider authClient={authClient as any} navigate={navigate} Link={CustomLink}>
      <AuthProvider>
        <CartProvider>
          <GTMPageTracker />
          <Routes>
            <Route path="/auth/sign-in" element={<RouteView><SignIn /></RouteView>} />
            <Route path="/auth/sign-up" element={<RouteView><SignUp /></RouteView>} />
            {/* Better Auth redirects here after the emailed verification link is
                clicked — see EMAIL_VERIFIED_CALLBACK_URL. Must stay unauthenticated. */}
            <Route path="/auth/verified" element={<RouteView><EmailVerified /></RouteView>} />
            <Route path="/auth/forgot-password" element={<AuthPageWrapper pathname="forgot-password" />} />
            <Route path="/auth/reset-password" element={<AuthPageWrapper pathname="reset-password" />} />

            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <RouteView>
                    <OnboardingGuard>
                      <Onboarding />
                    </OnboardingGuard>
                  </RouteView>
                </RequireAuth>
              }
            />

            <Route path="/invite" element={<RouteView><AcceptInvite /></RouteView>} />

            <Route path="/store/:tenantSlug" element={<RouteView><StorefrontPage /></RouteView>} />
            <Route path="/store/:tenantSlug/products" element={<RouteView><StorefrontProducts /></RouteView>} />
            <Route path="/store/:tenantSlug/products/:slug" element={<RouteView><StorefrontProductDetail /></RouteView>} />
            <Route path="/store/:tenantSlug/cart" element={<RouteView><StorefrontCart /></RouteView>} />
            <Route path="/store/:tenantSlug/checkout" element={<RouteView><StorefrontCheckout /></RouteView>} />
            <Route path="/store/:tenantSlug/order-confirmation" element={<RouteView><StorefrontOrderConfirmation /></RouteView>} />
            <Route path="/store/:tenantSlug/about" element={<RouteView><StorefrontCMSPage pageKey="about" /></RouteView>} />
            <Route path="/store/:tenantSlug/contact" element={<RouteView><StorefrontCMSPage pageKey="contact" /></RouteView>} />
            <Route path="/store/:tenantSlug/location" element={<RouteView><StorefrontCMSPage pageKey="location" /></RouteView>} />
            <Route path="/store/:tenantSlug/book" element={<RouteView><StorefrontBookingPage /></RouteView>} />
            <Route path="/store/:tenantSlug/bookings" element={<RouteView><StorefrontBookingPage /></RouteView>} />
            <Route path="/store/:tenantSlug/waiver" element={<RouteView><StorefrontWaiverPage /></RouteView>} />
            <Route path="/store/:tenantSlug/storefront" element={<RouteView><StorefrontCMSPage pageKey="storefront" /></RouteView>} />

            <Route path="/privacy" element={<RouteView><Privacy /></RouteView>} />
            <Route path="/terms" element={<RouteView><Terms /></RouteView>} />
            <Route path="/debug-auth" element={<RouteView><DebugAuth /></RouteView>} />

            <Route
              path="/:tenantId"
              element={
                <RequireAuth redirectTo="/auth/sign-in">
                  <AuthGuard>
                    <TenantLayout />
                  </AuthGuard>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<RouteView><TenantDashboardEntry /></RouteView>} />
              <Route path="products" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminProducts /></RequireAuth></RouteView>} />
              <Route path="orders" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminOrders /></RequireAuth></RouteView>} />
              <Route path="customers" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminCustomers /></RequireAuth></RouteView>} />
              <Route path="portfolio" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminPortfolio /></RequireAuth></RouteView>} />
              <Route path="events" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminEvents /></RequireAuth></RouteView>} />
              <Route path="event-mode" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><EventModeFallback /></RequireAuth></RouteView>} />
              <Route path="images" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminImages /></RequireAuth></RouteView>} />
              <Route path="settings" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminSettings /></RequireAuth></RouteView>} />
              <Route path="settings/website" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><WebsiteStorefront /></RequireAuth></RouteView>} />
              <Route path="settings/stuller" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><StullerCatalogPage /></RequireAuth></RouteView>} />
              <Route path="settings/payments" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><PaymentSettings /></RequireAuth></RouteView>} />
              <Route path="settings/billing" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><BillingSettings /></RequireAuth></RouteView>} />
              <Route path="analytics" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminAnalytics /></RequireAuth></RouteView>} />
              <Route path="feeds" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminFeeds /></RequireAuth></RouteView>} />
              <Route path="inventory" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><InventoryPage /></RequireAuth></RouteView>} />
              <Route path="supply-shop" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><SupplyShopPage /></RequireAuth></RouteView>} />
              <Route path="gift-cards" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminGiftCards /></RequireAuth></RouteView>} />
              <Route path="users" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminUsers /></RequireAuth></RouteView>} />
              <Route path="categories" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminCategories /></RequireAuth></RouteView>} />
              <Route path="promotions" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminPromotions /></RequireAuth></RouteView>} />
              <Route path="pos" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminPOS /></RequireAuth></RouteView>} />
              <Route path="transactions" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminTransactions /></RequireAuth></RouteView>} />
              <Route path="settings/storefront" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><StorefrontSettings /></RequireAuth></RouteView>} />
              <Route path="settings/waivers" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><WaiversSettings /></RequireAuth></RouteView>} />
              <Route path="settings/services" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><ServicesSettings /></RequireAuth></RouteView>} />
              <Route path="settings/addons" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AddonCatalogSettings /></RequireAuth></RouteView>} />
              <Route path="settings/availability" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AvailabilitySettings /></RequireAuth></RouteView>} />
              <Route path="settings/calendar-sync" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><CalendarSyncSettings /></RequireAuth></RouteView>} />
              <Route path="settings/notifications" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><NotificationsSettings /></RequireAuth></RouteView>} />
              <Route path="settings/backups" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><BackupSettings /></RequireAuth></RouteView>} />
              <Route path="settings/appearance" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AppearanceSettings /></RequireAuth></RouteView>} />
              <Route path="settings/about" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AboutSettings /></RequireAuth></RouteView>} />
              <Route path="settings/security" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><SecuritySettings /></RequireAuth></RouteView>} />
              <Route path="settings/workspaces" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><WorkspacesSettings /></RequireAuth></RouteView>} />
              <Route path="testimonials" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminTestimonials /></RequireAuth></RouteView>} />
              <Route path="reviews" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminReviews /></RequireAuth></RouteView>} />
              <Route path="reports" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><AdminReports /></RequireAuth></RouteView>} />
              <Route path="studio" element={<RouteView><RequireAuth roles={['admin', 'super_admin']}><StudioModePage /></RequireAuth></RouteView>} />
            </Route>

            <Route path="/" element={<RouteView><RootRedirect /></RouteView>} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </NeonAuthUIProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
